import { useEffect, useMemo, useState } from 'react';
import { getAvailableClinicians, client } from '../../services/api';
import type { AvailableClinicianResponse } from '../../services/api/types.gen';

interface UsePhysicianOptionsReturn {
  rogerPhysicianOptions: { name: string; id: string }[];
  loadingPhysicians: boolean;
}

/**
 * Fetches available clinicians for a clinic and returns the subset
 * flagged as physicians, formatted for dropdown use.
 */
export function usePhysicianOptions(
  agencyClinicId: string | null | undefined
): UsePhysicianOptionsReturn {
  const [availablePhysicians, setAvailablePhysicians] = useState<
    AvailableClinicianResponse[]
  >([]);
  const [loadingPhysicians, setLoadingPhysicians] = useState(false);

  useEffect(() => {
    if (!agencyClinicId) {
      setAvailablePhysicians([]);
      return;
    }

    let cancelled = false;

    const fetchPhysicians = async () => {
      setLoadingPhysicians(true);
      try {
        const response = await getAvailableClinicians({
          client,
          query: { clinic_id: agencyClinicId },
        });
        if (!cancelled && response?.data) {
          setAvailablePhysicians(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching available physicians:', err);
        }
      } finally {
        if (!cancelled) {
          setLoadingPhysicians(false);
        }
      }
    };

    fetchPhysicians();

    return () => {
      cancelled = true;
    };
  }, [agencyClinicId]);

  const rogerPhysicianOptions = useMemo(
    () =>
      availablePhysicians
        .filter(c => c.is_physician)
        .map(uc => {
          const name =
            uc.clinician_first_name && uc.clinician_last_name
              ? `${uc.clinician_first_name} ${uc.clinician_last_name}${uc.clinician_credentials ? `, ${uc.clinician_credentials}` : ''}`
              : `${uc.user_first_name} ${uc.user_last_name}`;
          return { name, id: uc.id };
        }),
    [availablePhysicians]
  );

  return { rogerPhysicianOptions, loadingPhysicians };
}
